import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
interface SlideProps {
  index: number;
  total: number;
}
function CountdownBadge({
  startSeconds
}: {
  startSeconds: number;
}) {
  const [remaining, setRemaining] = useState(startSeconds);
  useEffect(() => {
    const id = setInterval(() => {
      setRemaining(prev => prev > 1 ? prev - 1 : startSeconds);
    }, 1000);
    return () => clearInterval(id);
  }, [startSeconds]);
  return <div className="absolute top-3 right-3 z-10 flex items-baseline gap-1.5 px-3 py-1.5 rounded-md bg-black/65 backdrop-blur-sm border border-white/15" data-id="element-82">
      <span className="text-white/70 text-[clamp(0.55rem,0.8vw,0.7rem)] font-medium uppercase tracking-wide" data-id="element-83">
        Next episode in
      </span>
      <span className="text-white text-[clamp(0.9rem,1.4vw,1.15rem)] font-bold tabular-nums" data-id="element-84">
        {remaining}s
      </span>
    </div>;
}
export function Slide04Example({
  index
}: SlideProps) {
  const episodes = [{
    title: 'Where AD Hurts Most',
    speaker: 'Dr Simone Ribero',
    theme: 'Not all AD is equal. Location matters.',
    hook: '"Can 2% BSA have a greater impact than 20%?"',
    image: 'https://images.unsplash.com/photo-1579154204601-01588f351e67?w=800&q=80',
    countdown: 8
  }, {
    title: 'Beyond The Skin',
    speaker: 'Prof April Armstrong',
    theme: 'The burden of AD extends far beyond visible symptoms.',
    hook: '"What if the most severe symptoms aren\'t on the skin?"',
    image: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=800&q=80',
    countdown: 6
  }, {
    title: 'Barriers To Best Practice',
    speaker: 'Dr Raj Chovatiya',
    theme: 'Why access and outcomes vary dramatically.',
    hook: '"Why do similar patients experience different outcomes?"',
    image: 'https://images.unsplash.com/photo-1582719471384-894fbb16e074?w=800&q=80',
    countdown: 5
  }];
  return <div className="relative w-screen h-screen overflow-hidden bg-[var(--bg)] p-[clamp(2rem,4vw,4rem)]" data-id="element-85">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.2,
      duration: 0.6
    }} className="mb-[clamp(2rem,3vw,3rem)]" data-id="element-86">
        <p className="text-[var(--brand-green)] text-[clamp(0.875rem,1.5vw,1.125rem)] font-semibold uppercase tracking-wider mb-[clamp(0.5rem,1vw,1rem)]" data-id="element-87">
          Example: The June DERMX Webinar — a compelling series in 9 episodes
        </p>
        <h2 className="text-[var(--fg)] font-bold text-[clamp(2rem,4vw,3.5rem)] leading-[1.1] uppercase" data-id="element-88">
          <span className="text-[var(--brand-green-light)]" data-id="element-89">One</span> Webinar,{' '}
          <span className="text-[var(--brand-green-light)]" data-id="element-90">Multiple</span>{' '}
          Episodes
        </h2>
      </motion.div>

      {/* Episode cards */}
      <div className="grid grid-cols-3 gap-[clamp(1rem,2vw,2rem)] mb-[clamp(2rem,3vw,3rem)]" data-id="element-91">
        {episodes.map((episode, idx) => <motion.div key={idx} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3 + idx * 0.15,
        duration: 0.6
      }} className="group relative rounded-2xl overflow-hidden bg-[var(--surface)] aspect-[3/4]" data-id="element-92">
            <img src={episode.image} alt={episode.title} className="absolute inset-0 w-full h-full object-cover" data-id="element-93" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/20" data-id="element-94" />

            <CountdownBadge startSeconds={episode.countdown} data-id="element-95" />

            <div className="relative h-full flex flex-col justify-between p-[clamp(1.25rem,2vw,2rem)]" data-id="element-96">
              <div className="inline-flex items-center justify-center w-fit px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full" data-id="element-97">
                <span className="text-white text-xs font-semibold" data-id="element-98">
                  Episode {idx + 1}
                </span>
              </div>

              <div data-id="element-99">
                <h3 className="text-white font-bold text-[clamp(1.25rem,2vw,1.75rem)] leading-tight mb-2" data-id="element-100">
                  {episode.title}
                </h3>
                <p className="text-white/80 text-[clamp(0.875rem,1.25vw,1rem)] font-medium mb-3" data-id="element-101">
                  {episode.speaker}
                </p>
                <p className="text-white/70 text-[clamp(0.75rem,1.125vw,0.875rem)] mb-2" data-id="element-102">
                  <span className="font-semibold" data-id="element-103">Key theme:</span>{' '}
                  {episode.theme}
                </p>
                <p className="text-white/90 text-[clamp(0.875rem,1.25vw,1rem)] italic" data-id="element-104">
                  {episode.hook}
                </p>
              </div>
            </div>
          </motion.div>)}
      </div>

      {/* Bottom statement */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 0.8,
      duration: 0.6
    }} className="flex justify-between items-end" data-id="element-105">
        <p className="text-[var(--fg)] text-[clamp(1rem,1.75vw,1.5rem)] font-semibold" data-id="element-106">
          Each episode links directly to the full DERMX webinar.
        </p>
        <span className="text-[var(--muted)] text-sm" data-id="element-107">{index + 1}</span>
      </motion.div>
    </div>;
}